import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Droplets, Flame, CloudLightning, Home, Wind, Waves, AlertTriangle, CheckCircle2, DollarSign, ShieldCheck, TrendingUp, Phone } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  }
};
const iconMap: Record<string, any> = {
  'water-damage': Droplets,
  'fire-smoke-damage': Flame,
  'storm-hurricane': CloudLightning,
  'roof-claims': Home,
  'mold-damage': Wind,
  'flood-claims': Waves
};
const heroImages: Record<string, string> = {
  'water-damage': 'https://cdn.magicpatterns.com/uploads/mVfUtb4FtnVWQ7L3YuSrkY/Orca-Water-Damage-Restoration-1536x861.jpg',
  'fire-smoke-damage': 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
  'storm-hurricane': 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
  'roof-claims': 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
  'mold-damage': 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
  'flood-claims': 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80'
};
export function ServiceDetailPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    t
  } = useLanguage();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!slug || !t.serviceDetails[slug as keyof typeof t.serviceDetails]) {
    return <Navigate to="/" replace data-id="element-522" />;
  }
  const details = t.serviceDetails[slug as keyof typeof t.serviceDetails];
  const Icon = iconMap[slug] || AlertTriangle;
  return <div className="pt-20" data-id="element-523">
      {/* Hero Banner */}
      <section className="relative bg-brand-dark py-24 overflow-hidden" data-id="element-524">
        <div className="absolute inset-0 opacity-30 bg-cover bg-center" style={{
        backgroundImage: `url("${heroImages[slug] || heroImages['water-damage']}")`
      }} data-id="element-525" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/95 to-brand-dark/80" data-id="element-526" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-527">
          <Link to="/" className="inline-flex items-center gap-2 text-brand-gold hover:text-white transition-colors mb-8 font-semibold" data-id="element-528">
            <ArrowLeft size={20} data-id="element-529" />
            {t.free.back}
          </Link>

          <motion.div {...fadeIn} data-id="element-530">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-red/10 border border-brand-red/20 mb-6" data-id="element-531">
              <Icon className="text-brand-gold" size={20} data-id="element-532" />
              <span className="text-brand-gold font-semibold text-sm uppercase tracking-wider" data-id="element-533">
                {t.services.tag}
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6" data-id="element-534">
              {details.title}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed" data-id="element-535">
              {details.intro}
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is It & Causes */}
      <section className="py-20 bg-white" data-id="element-536">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-537">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-538">
            <motion.div {...fadeIn} data-id="element-539">
              <h2 className="text-brand-red font-bold tracking-wider uppercase mb-2" data-id="element-540">
                Understanding the Damage
              </h2>
              <h3 className="text-3xl font-black text-brand-dark mb-6" data-id="element-541">
                What Is It?
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-542">
                {details.whatIs}
              </p>
            </motion.div>

            <motion.div {...fadeIn} transition={{
            delay: 0.2
          }} className="bg-brand-gray p-8 rounded-sm" data-id="element-543">
              <h3 className="text-2xl font-bold text-brand-dark mb-6 flex items-center gap-3" data-id="element-544">
                <AlertTriangle className="text-brand-red" data-id="element-545" />
                Common Causes
              </h3>
              <ul className="space-y-4" data-id="element-546">
                {details.causes.map((cause: string, i: number) => <li key={i} className="flex items-start gap-3" data-id="element-547">
                    <div className="w-2 h-2 rounded-full bg-brand-red mt-2 shrink-0" data-id="element-548" />
                    <span className="text-gray-700 text-lg" data-id="element-549">{cause}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Coverage & Recovery */}
      <section className="py-20 bg-brand-dark text-white" data-id="element-550">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-551">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-552">
            <motion.div {...fadeIn} data-id="element-553">
              <h2 className="text-brand-gold font-bold tracking-wider uppercase mb-2" data-id="element-554">
                Policy Coverage
              </h2>
              <h3 className="text-3xl font-black mb-6" data-id="element-555">
                What Insurance Typically Covers
              </h3>
              <ul className="space-y-4 mb-8" data-id="element-556">
                {details.covered.map((item: string, i: number) => <li key={i} className="flex items-start gap-3" data-id="element-557">
                    <CheckCircle2 className="text-brand-gold shrink-0 mt-1" size={20} data-id="element-558" />
                    <span className="text-gray-300 text-lg" data-id="element-559">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div {...fadeIn} transition={{
            delay: 0.2
          }} className="bg-gradient-to-br from-brand-red to-brand-darkred p-8 rounded-sm shadow-2xl border border-white/10 relative overflow-hidden" data-id="element-560">
              <DollarSign className="absolute -right-10 -bottom-10 text-white/10 w-64 h-64" data-id="element-561" />
              <div className="relative z-10" data-id="element-562">
                <h3 className="text-xl font-bold text-white/90 mb-2 uppercase tracking-wider" data-id="element-563">
                  Average Recovery Range
                </h3>
                <div className="text-4xl md:text-5xl font-black text-brand-gold mb-4" data-id="element-564">
                  {details.recoveryRange}
                </div>
                <p className="text-white/80 leading-relaxed" data-id="element-565">
                  {details.recoveryNote}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why You Need Us & Case Study */}
      <section className="py-20 bg-white" data-id="element-566">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-567">
          <motion.div {...fadeIn} className="mb-16" data-id="element-568">
            <h2 className="text-brand-red font-bold tracking-wider uppercase mb-2" data-id="element-569">
              The Elite Advantage
            </h2>
            <h3 className="text-3xl font-black text-brand-dark mb-6" data-id="element-570">
              Why You Need a Public Adjuster
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl" data-id="element-571">
              {details.whyAdjuster}
            </p>
          </motion.div>

          <motion.div {...fadeIn} className="bg-brand-gray border-l-4 border-brand-gold p-8 rounded-r-sm" data-id="element-572">
            <div className="flex items-center gap-3 mb-6" data-id="element-573">
              <TrendingUp className="text-brand-gold" size={28} data-id="element-574" />
              <h3 className="text-2xl font-bold text-brand-dark" data-id="element-575">
                {details.caseTitle}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-576">
              <div className="bg-white p-6 rounded-sm shadow-sm border border-gray-100" data-id="element-577">
                <div className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-578">
                  Without Elite
                </div>
                <div className="text-xl font-bold text-brand-dark mb-2" data-id="element-579">
                  {details.caseBefore.split('(')[0]}
                </div>
                <div className="text-gray-600 text-sm" data-id="element-580">
                  ({details.caseBefore.split('(')[1]}
                </div>
              </div>
              <div className="bg-brand-dark p-6 rounded-sm shadow-md" data-id="element-581">
                <div className="text-sm font-bold text-brand-gold uppercase tracking-wider mb-2" data-id="element-582">
                  With Elite
                </div>
                <div className="text-xl font-bold text-white mb-2" data-id="element-583">
                  {details.caseAfter.split('(')[0]}
                </div>
                <div className="text-gray-400 text-sm" data-id="element-584">
                  ({details.caseAfter.split('(')[1]}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-red" data-id="element-585">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-586">
          <motion.div {...fadeIn} data-id="element-587">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6" data-id="element-588">
              {details.ctaTitle}
            </h2>
            <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-589">
              {details.ctaDesc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-590">
              <a href="tel:3054170530" className="inline-flex items-center justify-center gap-2 bg-white text-brand-red hover:bg-brand-gold hover:text-brand-dark px-8 py-4 rounded-sm font-bold text-lg transition-colors" data-id="element-591">
                <Phone size={22} data-id="element-592" />
                {t.nav.call} (305) 417-0530
              </a>
              <a href="tel:3057857829" className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-brand-red px-8 py-4 rounded-sm font-bold text-lg transition-colors" data-id="element-593">
                <Phone size={22} data-id="element-594" />
                {t.nav.call} (305) 785-7829
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}